import type { HTMLAttributes, ReactNode } from 'react';

type CardVariant = 'default' | 'hover' | 'highlighted' | 'danger' | 'ghost';
type CardPadding = 'none' | 'sm' | 'md' | 'lg';
type CardCorners = 'asymmetric' | 'asymmetric-inverse' | 'rounded' | 'sharp';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: CardVariant;
  padding?: CardPadding;
  corners?: CardCorners;
}

const variantStyles: Record<CardVariant, string> = {
  default: 'border-border bg-surface',
  hover: `
    border-border bg-surface
    hover:border-primary hover:bg-surface-hover
    hover:-translate-y-0.5 hover:shadow-glow
    transition-all duration-300 cursor-pointer
  `,
  highlighted: `
    border-primary bg-surface shadow-glow
  `,
  danger: `
    border-primary bg-surface
  `,
  ghost: `
    border-transparent bg-transparent
    hover:bg-surface-hover
    transition-all duration-300
  `,
};

const paddingStyles: Record<CardPadding, string> = {
  none: 'p-0',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

const cornerStyles: Record<CardCorners, string> = {
  'asymmetric': 'rounded-asymmetric-lg',
  'asymmetric-inverse': 'rounded-asymmetric-inverse-lg',
  'rounded': 'rounded-xl',
  'sharp': 'rounded-none',
};

export function Card({
  children,
  variant = 'default',
  padding = 'md',
  corners = 'asymmetric',
  className = '',
  ...props
}: CardProps) {
  return (
    <div
      className={`
        border
        ${cornerStyles[corners]}
        ${variantStyles[variant]}
        ${paddingStyles[padding]}
        ${className}
      `}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`mb-4 border-b border-border pb-4 ${className}`}>
      {children}
    </div>
  );
}

export function CardTitle({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={`text-lg font-semibold text-text-primary font-mono tracking-tight ${className}`}>
      {children}
    </h3>
  );
}

export function CardDescription({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <p className={`text-sm text-text-secondary mt-1 ${className}`}>
      {children}
    </p>
  );
}

export function CardContent({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={className}>{children}</div>;
}

export function CardFooter({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`mt-4 pt-4 border-t border-border flex items-center gap-3 ${className}`}>
      {children}
    </div>
  );
}
